import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioCharts = ({ chartData }) => {
  const [activeChart, setActiveChart] = useState('performance');

  const chartTabs = [
    { id: 'performance', label: 'Performance Trends', icon: 'TrendingUp' },
    { id: 'ltv', label: 'LTV Distribution', icon: 'PieChart' },
    { id: 'risk', label: 'Risk Analysis', icon: 'BarChart3' }
  ];

  const COLORS = ['#1E3A5F', '#4A90A4', '#F39C12', '#E74C3C', '#27AE60'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-dropdown">
          <p className="text-sm font-medium text-text-primary">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value}
              {entry?.name?.includes('%') ? '%' : entry?.name?.includes('Value') ? '' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderPerformanceChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartData?.performance}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis 
          dataKey="month" 
          stroke="var(--color-text-secondary)"
          fontSize={12}
        />
        <YAxis 
          stroke="var(--color-text-secondary)"
          fontSize={12}
          tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="portfolioValue" 
          stroke="var(--color-primary)" 
          strokeWidth={2}
          name="Portfolio Value"
          dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
        />
        <Line 
          type="monotone" 
          dataKey="marketValue" 
          stroke="var(--color-secondary)" 
          strokeWidth={2}
          name="Market Value"
          dot={{ fill: 'var(--color-secondary)', strokeWidth: 2, r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderLTVChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        <Pie
          data={chartData?.ltvDistribution}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
          outerRadius={120}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData?.ltvDistribution?.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderRiskChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData?.riskAnalysis}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis 
          dataKey="category" 
          stroke="var(--color-text-secondary)"
          fontSize={12}
        />
        <YAxis 
          stroke="var(--color-text-secondary)"
          fontSize={12}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar 
          dataKey="count" 
          fill="var(--color-primary)"
          name="Asset Count"
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="value" 
          fill="var(--color-secondary)"
          name="Total Value ($M)"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderChart = () => {
    switch (activeChart) {
      case 'performance':
        return renderPerformanceChart();
      case 'ltv':
        return renderLTVChart();
      case 'risk':
        return renderRiskChart();
      default:
        return renderPerformanceChart();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Portfolio Analytics</h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Charts
          </Button>
        </div>
      </div>
      <div className="flex space-x-1 mb-6 bg-muted p-1 rounded-lg">
        {chartTabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveChart(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
              activeChart === tab?.id
                ? 'bg-surface text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      <div className="w-full" aria-label={`${chartTabs?.find(tab => tab?.id === activeChart)?.label} Chart`}>
        {renderChart()}
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-muted rounded-lg">
          <div className="text-2xl font-semibold text-text-primary">
            {chartData?.insights?.totalAssets}
          </div>
          <div className="text-sm text-text-secondary">Total Assets</div>
        </div>
        <div className="text-center p-4 bg-muted rounded-lg">
          <div className="text-2xl font-semibold text-text-primary">
            {chartData?.insights?.avgLTV}%
          </div>
          <div className="text-sm text-text-secondary">Average LTV</div>
        </div>
        <div className="text-center p-4 bg-muted rounded-lg">
          <div className="text-2xl font-semibold text-text-primary">
            ${(chartData?.insights?.totalValue / 1000000)?.toFixed(1)}M
          </div>
          <div className="text-sm text-text-secondary">Total Value</div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioCharts;